import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import categoryService from '../services/categoryService';
import accountService from '../services/accountService';
import { formatError } from '../utils/errorHelper';
import IconSelector from '../components/IconSelector';
import ColorPicker from '../components/ColorPicker';
import { getIconComponent } from '../utils/iconHelper';
import { MdEdit, MdDelete, MdCheckCircle } from 'react-icons/md';
import logo from '../assets/images/logo-redonda.png';
import './Setup.css';

function Setup() {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState('welcome');
  const [categories, setCategories] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [notifications, setNotifications] = useState([]);

  // Estado do formulário de categoria
  const [showCategoryForm, setShowCategoryForm] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [categoryFormData, setCategoryFormData] = useState({
    name: '',
    slug: '',
    type: 'expense',
    icon: 'category',
    color: '#E3A864',
    is_active: true,
  });

  // Estado do formulário de conta
  const [showAccountForm, setShowAccountForm] = useState(false);
  const [editingAccount, setEditingAccount] = useState(null);
  const [accountFormData, setAccountFormData] = useState({
    name: '',
    initial_balance: '',
    type: 'checking',
    color: '#10243C',
    description: '',
    notes: '',
    is_active: true,
  });

  const accountTypes = {
    checking: 'Conta Corrente',
    savings: 'Poupança',
    investment: 'Investimento',
    cash: 'Dinheiro',
    credit_card: 'Cartão de Crédito',
    other: 'Outro',
  };

  useEffect(() => {
    if (currentStep === 'categories') {
      loadCategories();
    } else if (currentStep === 'accounts') {
      loadAccounts();
    }
  }, [currentStep]);

  // Função para adicionar notificação
  const addNotification = (type, title, message, duration = 5000) => {
    const id = Date.now();
    const notification = { id, type, title, message };
    
    setNotifications(prev => [...prev, notification]);
    
    if (duration > 0) {
      setTimeout(() => {
        removeNotification(id);
      }, duration);
    }
  };

  const removeNotification = (id) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  };

  // Carregar categorias
  const loadCategories = async () => {
    setLoading(true);
    try {
      const data = await categoryService.getCategories();
      setCategories(data.data || data);
    } catch (error) {
      console.error('Erro ao carregar categorias:', error);
      addNotification('error', 'Erro', 'Erro ao carregar categorias.', 6000);
    } finally {
      setLoading(false);
    }
  };

  // Carregar contas
  const loadAccounts = async () => {
    setLoading(true);
    try {
      const data = await accountService.getAccounts();
      setAccounts(data.data || data);
    } catch (error) {
      console.error('Erro ao carregar contas:', error);
      addNotification('error', 'Erro', 'Erro ao carregar contas.', 6000);
    } finally {
      setLoading(false);
    }
  };

  // Handlers de categoria
  const handleCategoryFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newValue = type === 'checkbox' ? checked : value;
    
    setCategoryFormData({
      ...categoryFormData,
      [name]: newValue,
    });

    if (name === 'name' && !editingCategory) {
      const slug = value
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/(^-|-$)/g, '');
      setCategoryFormData((prev) => ({ ...prev, slug }));
    }
  };

  const handleCategorySubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingCategory) {
        await categoryService.updateCategory(editingCategory.id, categoryFormData);
        addNotification('success', 'Sucesso', 'Categoria atualizada!', 3000);
      } else {
        await categoryService.createCategory(categoryFormData);
        addNotification('success', 'Sucesso', 'Categoria criada!', 3000);
      }
      setShowCategoryForm(false);
      setEditingCategory(null);
      resetCategoryForm();
      loadCategories();
    } catch (error) {
      console.error('Erro ao salvar categoria:', error);
      addNotification('error', 'Erro', formatError(error, 'Erro ao salvar categoria.'), 6000);
    }
  };

  const handleEditCategory = (category) => {
    setEditingCategory(category);
    setCategoryFormData({
      name: category.name,
      slug: category.slug,
      type: category.type,
      icon: category.icon || 'category',
      color: category.color || '#E3A864',
      is_active: category.is_active,
    });
    setShowCategoryForm(true);
  };

  const handleDeleteCategory = async (id) => {
    if (window.confirm('Deseja realmente excluir esta categoria?')) {
      try {
        await categoryService.deleteCategory(id);
        addNotification('success', 'Sucesso', 'Categoria excluída!', 3000);
        loadCategories();
      } catch (error) {
        console.error('Erro ao excluir categoria:', error);
        addNotification('error', 'Erro', formatError(error, 'Erro ao excluir categoria.'), 6000);
      }
    }
  };

  const resetCategoryForm = () => {
    setCategoryFormData({
      name: '',
      slug: '',
      type: 'expense',
      icon: 'category',
      color: '#E3A864',
      is_active: true,
    });
  };

  // Handlers de conta
  const handleAccountFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setAccountFormData({
      ...accountFormData,
      [name]: type === 'checkbox' ? checked : value,
    });
  };

  const handleAccountSubmit = async (e) => {
    e.preventDefault();
    try {
      const dataToSend = {
        ...accountFormData,
        initial_balance: parseFloat(accountFormData.initial_balance) || 0,
      };

      if (editingAccount) {
        await accountService.updateAccount(editingAccount.id, dataToSend);
        addNotification('success', 'Sucesso', 'Conta atualizada!', 3000);
      } else {
        await accountService.createAccount(dataToSend);
        addNotification('success', 'Sucesso', 'Conta criada!', 3000);
      }

      setShowAccountForm(false);
      setEditingAccount(null);
      resetAccountForm();
      loadAccounts();
    } catch (error) {
      console.error('Erro ao salvar conta:', error);
      addNotification('error', 'Erro', formatError(error, 'Erro ao salvar conta.'), 6000);
    }
  };

  const handleEditAccount = (account) => {
    setEditingAccount(account);
    setAccountFormData({
      name: account.name,
      initial_balance: account.initial_balance.toString(),
      type: account.type,
      color: account.color || '#10243C',
      description: account.description || '',
      notes: account.notes || '',
      is_active: account.is_active,
    });
    setShowAccountForm(true);
  };

  const handleDeleteAccount = async (id) => {
    if (window.confirm('Deseja realmente excluir esta conta?')) {
      try {
        await accountService.deleteAccount(id);
        addNotification('success', 'Sucesso', 'Conta excluída!', 3000);
        loadAccounts();
      } catch (error) {
        console.error('Erro ao excluir conta:', error);
        addNotification('error', 'Erro', formatError(error, 'Erro ao excluir conta.'), 6000);
      }
    }
  };

  const resetAccountForm = () => {
    setAccountFormData({
      name: '',
      initial_balance: '',
      type: 'checking',
      color: '#10243C',
      description: '',
      notes: '',
      is_active: true,
    });
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value);
  };

  const handleFinishSetup = () => {
    navigate('/dashboard');
  };

  // Renderização da etapa de boas-vindas
  const renderWelcome = () => (
    <div className="setup-welcome">
      <div className="welcome-header">
        <img src={logo} alt="Finanças 360" className="welcome-logo" />
        <h1>Bem-vindo ao Finanças 360!</h1>
        <p>Vamos configurar sua conta em poucos passos</p>
      </div>

      <div className="welcome-content">
        <div className="welcome-card">
          <div className="welcome-icon">📊</div>
          <h3>Organize suas categorias</h3>
          <p>Visualize e personalize as categorias de receitas e despesas</p>
        </div>

        <div className="welcome-card">
          <div className="welcome-icon">💰</div>
          <h3>Configure suas contas</h3>
          <p>Adicione suas contas bancárias, cartões e outras fontes</p>
        </div>

        <div className="welcome-card">
          <div className="welcome-icon">🚀</div>
          <h3>Comece a usar</h3>
          <p>Tudo pronto para gerenciar suas finanças!</p>
        </div>
      </div>

      <div className="welcome-actions">
        <button className="btn-primary btn-large" onClick={() => setCurrentStep('categories')}>
          Começar Configuração
        </button>
        <button className="btn-link" onClick={handleFinishSetup}>
          Pular e ir para o dashboard
        </button>
      </div>
    </div>
  );

  // Renderização da etapa de categorias
  const renderCategories = () => (
    <div className="setup-step">
      <div className="step-header">
        <h2>Categorias</h2>
        <p>Visualize as categorias existentes e adicione novas se desejar</p>
      </div>

      <div className="step-actions">
        <button className="btn-secondary" onClick={() => setShowCategoryForm(true)}>
          + Adicionar Categoria
        </button>
      </div>

      {loading ? (
        <div className="loading">
          <div className="spinner"></div>
          <p>Carregando...</p>
        </div>
      ) : (
        <>
          <div className="categories-section">
            <h3>Receitas ({categories.filter(c => c.type === 'income').length})</h3>
            <div className="categories-list">
              {categories.filter(c => c.type === 'income').length === 0 ? (
                <p className="empty-message">Nenhuma categoria de receita cadastrada</p>
              ) : (
                categories.filter(c => c.type === 'income').map((category) => (
                  <div key={category.id} className="category-item">
                    <div className="category-info">
                      <div
                        className="category-icon"
                        style={{ backgroundColor: category.color }}
                      >
                        {(() => {
                          const IconComponent = getIconComponent(category.icon);
                          return <IconComponent size={20} color="white" />;
                        })()}
                      </div>
                      <span>{category.name}</span>
                    </div>
                    <div className="category-actions">
                      <button className="btn-icon" onClick={() => handleEditCategory(category)}>
                        <MdEdit />
                      </button>
                      <button className="btn-icon btn-delete" onClick={() => handleDeleteCategory(category.id)}>
                        <MdDelete />
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          <div className="categories-section">
            <h3>Despesas ({categories.filter(c => c.type === 'expense').length})</h3>
            <div className="categories-list">
              {categories.filter(c => c.type === 'expense').length === 0 ? (
                <p className="empty-message">Nenhuma categoria de despesa cadastrada</p>
              ) : (
                categories.filter(c => c.type === 'expense').map((category) => (
                  <div key={category.id} className="category-item">
                    <div className="category-info">
                      <div
                        className="category-icon"
                        style={{ backgroundColor: category.color }}
                      >
                        {(() => {
                          const IconComponent = getIconComponent(category.icon);
                          return <IconComponent size={20} color="white" />;
                        })()}
                      </div>
                      <span>{category.name}</span>
                    </div>
                    <div className="category-actions">
                      <button className="btn-icon" onClick={() => handleEditCategory(category)}>
                        <MdEdit />
                      </button>
                      <button className="btn-icon btn-delete" onClick={() => handleDeleteCategory(category.id)}>
                        <MdDelete />
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </>
      )}

      <div className="step-navigation">
        <button className="btn-secondary" onClick={() => setCurrentStep('welcome')}>
          Voltar
        </button>
        <button className="btn-primary" onClick={() => setCurrentStep('accounts')}>
          Próximo: Contas
        </button>
      </div>
    </div>
  );

  // Renderização da etapa de contas
  const renderAccounts = () => (
    <div className="setup-step">
      <div className="step-header">
        <h2>Contas Bancárias</h2>
        <p>Adicione suas contas bancárias, cartões e outras fontes de recursos</p>
      </div>

      <div className="step-actions">
        <button className="btn-secondary" onClick={() => setShowAccountForm(true)}>
          + Adicionar Conta
        </button>
      </div>

      {loading ? (
        <div className="loading">
          <div className="spinner"></div>
          <p>Carregando...</p>
        </div>
      ) : (
        <div className="accounts-list">
          {accounts.length === 0 ? (
            <div className="empty-state">
              <p>Nenhuma conta cadastrada ainda</p>
              <p className="empty-subtitle">Adicione sua primeira conta para começar</p>
            </div>
          ) : (
            accounts.map((account) => (
              <div key={account.id} className="account-item">
                <div className="account-info">
                  <div
                    className="account-color-bar"
                    style={{ backgroundColor: account.color }}
                  />
                  <div className="account-details">
                    <h4>{account.name}</h4>
                    <span className="account-type">{accountTypes[account.type]}</span>
                    {account.description && (
                      <p className="account-description">{account.description}</p>
                    )}
                  </div>
                </div>
                <div className="account-balance">
                  <span className="balance-label">Saldo Inicial</span>
                  <span className="balance-value">{formatCurrency(account.initial_balance)}</span>
                </div>
                <div className="account-actions">
                  <button className="btn-icon" onClick={() => handleEditAccount(account)}>
                    <MdEdit />
                  </button>
                  <button className="btn-icon btn-delete" onClick={() => handleDeleteAccount(account.id)}>
                    <MdDelete />
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      <div className="step-navigation">
        <button className="btn-secondary" onClick={() => setCurrentStep('categories')}>
          Voltar
        </button>
        <button className="btn-primary" onClick={() => setCurrentStep('complete')}>
          Finalizar
        </button>
      </div>
    </div>
  );

  // Renderização da etapa de conclusão
  const renderComplete = () => (
    <div className="setup-complete">
      <div className="complete-icon">
        <MdCheckCircle size={80} color="#4CAF50" />
      </div>
      <h1>Configuração Concluída!</h1>
      <p>Sua conta está pronta para uso</p>

      <div className="complete-summary">
        <div className="summary-item">
          <span className="summary-label">Categorias</span>
          <span className="summary-value">{categories.length}</span>
        </div>
        <div className="summary-item">
          <span className="summary-label">Contas</span>
          <span className="summary-value">{accounts.length}</span>
        </div>
      </div>

      <div className="complete-actions">
        <button className="btn-primary btn-large" onClick={handleFinishSetup}>
          Ir para o Dashboard
        </button>
      </div>
    </div>
  );

  // Modal de formulário de categoria
  const renderCategoryFormModal = () => (
    showCategoryForm && (
      <div className="modal">
        <div className="modal-content">
          <div className="modal-header">
            <h2>{editingCategory ? 'Editar' : 'Nova'} Categoria</h2>
            <button
              className="close-btn"
              onClick={() => {
                setShowCategoryForm(false);
                setEditingCategory(null);
                resetCategoryForm();
              }}
            >
              ✕
            </button>
          </div>
          <form className="setup-form" onSubmit={handleCategorySubmit}>
            <div className="form-body">
              <div className="form-group">
                <label>Nome</label>
                <input
                  type="text"
                  name="name"
                  value={categoryFormData.name}
                  onChange={handleCategoryFormChange}
                  required
                  placeholder="Ex: Alimentação"
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Tipo</label>
                  <select name="type" value={categoryFormData.type} onChange={handleCategoryFormChange}>
                    <option value="expense">Despesa</option>
                    <option value="income">Receita</option>
                  </select>
                </div>
                <ColorPicker
                  value={categoryFormData.color}
                  onChange={(color) => setCategoryFormData({ ...categoryFormData, color })}
                  label="Cor"
                />
              </div>

              <IconSelector
                selectedIcon={categoryFormData.icon}
                onSelectIcon={(icon) => setCategoryFormData({ ...categoryFormData, icon })}
                type={categoryFormData.type}
              />
            </div>

            <div className="form-actions">
              <button
                type="button"
                className="btn-secondary"
                onClick={() => {
                  setShowCategoryForm(false);
                  setEditingCategory(null);
                  resetCategoryForm();
                }}
              >
                Cancelar
              </button>
              <button type="submit" className="btn-primary">
                {editingCategory ? 'Atualizar' : 'Criar'}
              </button>
            </div>
          </form>
        </div>
      </div>
    )
  );

  // Modal de formulário de conta
  const renderAccountFormModal = () => (
    showAccountForm && (
      <div className="modal">
        <div className="modal-content">
          <div className="modal-header">
            <h2>{editingAccount ? 'Editar' : 'Nova'} Conta</h2>
            <button
              className="close-btn"
              onClick={() => {
                setShowAccountForm(false);
                setEditingAccount(null);
                resetAccountForm();
              }}
            >
              ✕
            </button>
          </div>
          <form className="setup-form" onSubmit={handleAccountSubmit}>
            <div className="form-body">
              <div className="form-group">
                <label>Nome da Conta</label>
                <input
                  type="text"
                  name="name"
                  value={accountFormData.name}
                  onChange={handleAccountFormChange}
                  required
                  placeholder="Ex: Banco do Brasil - Conta Corrente"
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Tipo</label>
                  <select name="type" value={accountFormData.type} onChange={handleAccountFormChange}>
                    {Object.entries(accountTypes).map(([key, label]) => (
                      <option key={key} value={key}>
                        {label}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Saldo Inicial</label>
                  <input
                    type="number"
                    step="0.01"
                    name="initial_balance"
                    value={accountFormData.initial_balance}
                    onChange={handleAccountFormChange}
                    required
                    placeholder="0,00"
                  />
                </div>
              </div>

              <ColorPicker
                value={accountFormData.color}
                onChange={(color) => setAccountFormData({ ...accountFormData, color })}
                label="Cor"
              />

              <div className="form-group">
                <label>Descrição (opcional)</label>
                <input
                  type="text"
                  name="description"
                  value={accountFormData.description}
                  onChange={handleAccountFormChange}
                  placeholder="Descrição breve da conta"
                />
              </div>
            </div>

            <div className="form-actions">
              <button
                type="button"
                className="btn-secondary"
                onClick={() => {
                  setShowAccountForm(false);
                  setEditingAccount(null);
                  resetAccountForm();
                }}
              >
                Cancelar
              </button>
              <button type="submit" className="btn-primary">
                {editingAccount ? 'Atualizar' : 'Criar'}
              </button>
            </div>
          </form>
        </div>
      </div>
    )
  );

  return (
    <div className="setup-container">
      <div className="setup-background">
        <div className="shape shape-1"></div>
        <div className="shape shape-2"></div>
        <div className="shape shape-3"></div>
      </div>

      <div className="setup-content">
        {currentStep === 'welcome' && renderWelcome()}
        {currentStep === 'categories' && renderCategories()}
        {currentStep === 'accounts' && renderAccounts()}
        {currentStep === 'complete' && renderComplete()}
      </div>

      {renderCategoryFormModal()}
      {renderAccountFormModal()}

      {/* Toast Notifications */}
      <div className="toast-container">
        {notifications.map((notification) => (
          <div key={notification.id} className={`toast ${notification.type}`}>
            <div className="toast-icon">
              {notification.type === 'success' && '✓'}
              {notification.type === 'error' && '✕'}
              {notification.type === 'warning' && '⚠'}
              {notification.type === 'info' && 'ℹ'}
            </div>
            <div className="toast-content">
              <h4 className="toast-title">{notification.title}</h4>
              <p className="toast-message">{notification.message}</p>
            </div>
            <button 
              className="toast-close" 
              onClick={() => removeNotification(notification.id)}
              aria-label="Fechar"
            >
              ✕
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

export default Setup;
